import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { api } from '../utils/api';
import { Printer, ArrowLeft } from 'lucide-react';

const MASCOT_URL =
  'https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png';

const WorksheetPrintPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.worksheetGet(id);
        setWs(data);
      } catch (e) {
        navigate('/worksheets');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  if (loading || !ws) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-slate-500">
        Loading worksheet…
      </div>
    );
  }

  const letters = ['A', 'B', 'C', 'D'];
  const createdDate = new Date(ws.created_at).toLocaleDateString('en-SG', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });

  // Deep-link URL encoded into the QR code — jumps parents straight to the
  // upload flow for THIS specific worksheet. Uses window.location.origin so it
  // works in preview + production without any env change.
  const uploadUrl = `${window.location.origin}/worksheets/${ws.id}?upload=1`;

  return (
    <div className="worksheet-print-root">
      {/* Toolbar (hidden when printing) */}
      <div className="worksheet-toolbar no-print">
        <button
          onClick={() => navigate(`/worksheets/${id}`)}
          className="flex items-center gap-1.5 text-slate-600 hover:text-emerald-700 font-semibold text-sm"
          data-testid="worksheet-print-back"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <button
          onClick={() => window.print()}
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-5 py-2.5 rounded-full flex items-center gap-2"
          data-testid="worksheet-print-trigger"
        >
          <Printer size={16} /> Print / Save as PDF
        </button>
      </div>

      {/* ------ Page 1: Question Paper ------ */}
      <div className="worksheet-page">
        <div className="ws-header">
          <div className="ws-brand">
            <img src={MASCOT_URL} alt="Prof.Scaly" className="ws-mascot" />
            <div>
              <div className="ws-brand-name">Prof.Scaly</div>
              <div className="ws-brand-tag">PSLE English Practice</div>
            </div>
          </div>
          <div className="ws-meta">
            <div className="ws-meta-row"><strong>Worksheet</strong> · {ws.type_label}</div>
            <div className="ws-meta-row"><strong>Level:</strong> {ws.level}</div>
            <div className="ws-meta-row"><strong>Date:</strong> {createdDate}</div>
            <div className="ws-meta-row ws-code">ID: {ws.id.slice(0, 8)}</div>
          </div>
        </div>

        <div className="ws-student-row">
          <div>Name: <span className="ws-underline" /></div>
          <div>Class: <span className="ws-underline short" /></div>
          <div>Time: 20 min</div>
        </div>

        <div className="ws-instructions">
          <strong>Instructions:</strong> For each question, shade the bubble on the OCR Answer Sheet (last page) that
          corresponds to your chosen option. Use a dark pencil or black pen. Do not tick or cross the bubbles — shade
          them fully.
        </div>

        <ol className="ws-questions">
          {ws.questions.map((q) => (
            <li key={q.number} className="ws-question">
              <div className="ws-q-text">
                <span className="ws-q-num">{q.number}.</span>
                <span className="ws-q-body">{q.question_text}</span>
              </div>
              <div className="ws-options">
                {q.options.map((opt, i) => (
                  <div key={i} className="ws-option">
                    <span className="ws-option-letter">({letters[i]})</span>
                    <span className="ws-option-text">{opt}</span>
                  </div>
                ))}
              </div>
            </li>
          ))}
        </ol>

        <div className="ws-footer">
          Prof.Scaly · PSLE English Practice · Aligned to the Singapore MOE syllabus
        </div>
      </div>

      {/* ------ Page 2: OCR Bubble Answer Sheet ------ */}
      <div className="worksheet-page ws-answer-sheet-page">
        <div className="ws-header">
          <div className="ws-brand">
            <img src={MASCOT_URL} alt="Prof.Scaly" className="ws-mascot" />
            <div>
              <div className="ws-brand-name">Prof.Scaly</div>
              <div className="ws-brand-tag">OCR Answer Sheet</div>
            </div>
          </div>
          <div className="ws-meta">
            <div className="ws-meta-row"><strong>{ws.type_label}</strong></div>
            <div className="ws-meta-row"><strong>Level:</strong> {ws.level}</div>
            <div className="ws-meta-row ws-code">ID: {ws.id.slice(0, 8)}</div>
          </div>
        </div>

        <div className="ws-student-row">
          <div>Name: <span className="ws-underline" /></div>
          <div>Date: <span className="ws-underline short" /></div>
        </div>

        <div className="ws-instructions">
          <strong>How to submit:</strong> After your child completes this sheet, place it on a flat, well-lit surface.
          Snap a clear photo (the whole sheet visible, no tilt). Log in to Prof.Scaly and tap
          <em> Upload photo of answer sheet</em> on this worksheet.
        </div>

        {/* QR quick-upload panel */}
        <div className="ws-qr-panel">
          <div className="ws-qr-code" data-testid="worksheet-print-qr">
            <QRCodeSVG value={uploadUrl} size={110} level="M" includeMargin={false} />
          </div>
          <div className="ws-qr-copy">
            <div className="ws-qr-title">📱 Scan to upload</div>
            <div className="ws-qr-body">
              Scan this QR code with your phone camera. It opens the upload page for
              <strong> this exact worksheet</strong> — no searching needed. Log in, snap a photo of the sheet
              below, and Prof.Scaly will grade it in seconds.
            </div>
          </div>
        </div>

        {/* Alignment marks (help OCR find the sheet corners) */}
        <div className="ws-anchor-row">
          <span className="ws-anchor" />
          <span className="ws-anchor-title">ANSWER SHEET · SHADE ONE BUBBLE PER ROW</span>
          <span className="ws-anchor" />
        </div>

        <table className="ws-bubble-table">
          <thead>
            <tr>
              <th>Q</th>
              <th>A</th>
              <th>B</th>
              <th>C</th>
              <th>D</th>
              <th>Q</th>
              <th>A</th>
              <th>B</th>
              <th>C</th>
              <th>D</th>
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2, 3, 4].map((row) => {
              const left = row + 1;
              const right = row + 6;
              return (
                <tr key={row}>
                  <td className="ws-q-num-cell">{left}</td>
                  {letters.map((L) => (
                    <td key={`L${L}`}>
                      <span className="ws-bubble">{L}</span>
                    </td>
                  ))}
                  <td className="ws-q-num-cell">{right}</td>
                  {letters.map((L) => (
                    <td key={`R${L}`}>
                      <span className="ws-bubble">{L}</span>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="ws-anchor-row">
          <span className="ws-anchor" />
          <span className="ws-anchor-title">END OF ANSWER SHEET</span>
          <span className="ws-anchor" />
        </div>

        <div className="ws-footer">
          Prof.Scaly · Auto-graded via prof-scaly.com after upload
        </div>
      </div>

      {/* Print + screen styles */}
      <style>{`
        .worksheet-print-root {
          background: #f1f5f9;
          min-height: 100vh;
          padding: 24px 12px 60px;
          color: #0f172a;
        }
        .worksheet-toolbar {
          max-width: 794px;
          margin: 0 auto 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 12px;
        }
        .worksheet-page {
          background: white;
          width: 794px;
          max-width: 100%;
          min-height: 1123px;
          margin: 0 auto 20px;
          padding: 44px 48px 40px;
          box-sizing: border-box;
          box-shadow: 0 4px 24px rgba(15, 23, 42, 0.08);
          font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
          font-size: 13px;
          line-height: 1.5;
          position: relative;
        }
        .ws-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          border-bottom: 3px solid #10b981;
          padding-bottom: 12px;
          margin-bottom: 14px;
        }
        .ws-brand {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .ws-mascot {
          width: 52px;
          height: 52px;
          object-fit: contain;
        }
        .ws-brand-name {
          font-size: 20px;
          font-weight: 900;
          color: #047857;
          font-family: 'Fredoka', 'Inter', sans-serif;
          line-height: 1;
        }
        .ws-brand-tag {
          font-size: 11px;
          color: #64748b;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-top: 2px;
        }
        .ws-meta {
          font-size: 11px;
          text-align: right;
          color: #334155;
        }
        .ws-meta-row {
          margin-bottom: 2px;
        }
        .ws-code {
          font-family: 'Menlo', 'Monaco', monospace;
          color: #94a3b8;
          font-size: 10px;
        }
        .ws-student-row {
          display: flex;
          gap: 24px;
          font-size: 12px;
          font-weight: 600;
          color: #334155;
          margin-bottom: 14px;
        }
        .ws-student-row > div {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .ws-underline {
          flex: 1;
          border-bottom: 1px solid #94a3b8;
          height: 16px;
          display: inline-block;
        }
        .ws-underline.short {
          max-width: 120px;
        }
        .ws-instructions {
          font-size: 11.5px;
          background: #ecfdf5;
          border: 1px dashed #6ee7b7;
          border-radius: 8px;
          padding: 10px 14px;
          margin-bottom: 18px;
          color: #065f46;
          line-height: 1.5;
        }
        .ws-qr-panel {
          display: flex;
          align-items: center;
          gap: 14px;
          border: 2px solid #0f172a;
          border-radius: 10px;
          padding: 12px 14px;
          margin-bottom: 18px;
          background: #f8fafc;
        }
        .ws-qr-code {
          flex-shrink: 0;
          background: white;
          padding: 6px;
          border-radius: 6px;
          border: 1px solid #cbd5e1;
          line-height: 0;
        }
        .ws-qr-copy {
          flex: 1;
        }
        .ws-qr-title {
          font-size: 13px;
          font-weight: 900;
          color: #0f172a;
          margin-bottom: 4px;
        }
        .ws-qr-body {
          font-size: 11px;
          line-height: 1.5;
          color: #334155;
        }
        .ws-qr-body strong {
          color: #047857;
        }
        .ws-questions {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .ws-question {
          margin-bottom: 14px;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .ws-q-text {
          display: flex;
          gap: 8px;
          font-weight: 600;
          font-size: 13px;
          color: #0f172a;
          margin-bottom: 6px;
        }
        .ws-q-num {
          font-weight: 900;
          color: #047857;
          min-width: 24px;
        }
        .ws-q-body {
          flex: 1;
          white-space: pre-wrap;
        }
        .ws-options {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4px 20px;
          padding-left: 32px;
          font-size: 12.5px;
          color: #1f2937;
        }
        .ws-option {
          display: flex;
          gap: 6px;
          padding: 2px 0;
        }
        .ws-option-letter {
          font-weight: 800;
          color: #475569;
          min-width: 22px;
        }
        .ws-footer {
          position: absolute;
          bottom: 22px;
          left: 48px;
          right: 48px;
          text-align: center;
          font-size: 10px;
          color: #94a3b8;
          border-top: 1px solid #e2e8f0;
          padding-top: 8px;
        }
        .ws-anchor-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin: 8px 0 14px;
        }
        .ws-anchor {
          width: 18px;
          height: 18px;
          background: #0f172a;
          border-radius: 2px;
        }
        .ws-anchor-title {
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.15em;
          color: #0f172a;
          text-transform: uppercase;
        }
        .ws-bubble-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }
        .ws-bubble-table th {
          background: #0f172a;
          color: white;
          font-size: 11px;
          font-weight: 800;
          padding: 6px 0;
          text-align: center;
          letter-spacing: 0.1em;
        }
        .ws-bubble-table th:first-child,
        .ws-bubble-table th:nth-child(6) {
          background: #047857;
        }
        .ws-bubble-table td {
          border: 1px solid #cbd5e1;
          text-align: center;
          padding: 10px 0;
        }
        .ws-q-num-cell {
          font-weight: 900;
          color: #047857;
          background: #f0fdf4;
          width: 40px;
          font-size: 14px;
        }
        .ws-bubble {
          display: inline-flex;
          width: 24px;
          height: 24px;
          align-items: center;
          justify-content: center;
          border: 2px solid #0f172a;
          border-radius: 50%;
          font-size: 11px;
          font-weight: 900;
          color: #0f172a;
          background: white;
        }
        .ws-answer-key {
          margin-top: 20px;
          border-top: 1px dashed #cbd5e1;
          padding-top: 12px;
        }
        .ws-answer-key-title {
          font-size: 10.5px;
          font-weight: 800;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 8px;
        }
        .ws-answer-key-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 4px 12px;
          font-size: 11px;
          color: #334155;
        }
        .ws-answer-key-cell strong {
          color: #047857;
          margin-right: 4px;
        }
        /* -- PRINT -- */
        @media print {
          .no-print { display: none !important; }
          .worksheet-print-root {
            background: white;
            padding: 0;
          }
          .worksheet-page {
            box-shadow: none;
            margin: 0;
            width: 100%;
            min-height: auto;
            padding: 20mm 15mm;
            page-break-after: always;
          }
          .worksheet-page:last-child {
            page-break-after: auto;
          }
          @page {
            size: A4;
            margin: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default WorksheetPrintPage;
